/**
 * graft/A5: typeahead + did-you-mean for the AI search box.
 *
 * Backed by the search service's AUTHED suggest endpoint
 * ({searchBaseUrl}v2/search/suggest — the production TenantEntityIndex
 * prefix/trigram/phonetic layers plus a title scan; tenancy from the JWT).
 *
 * GRAFT-ONLY (ADO 289472): suggest exists only on the v2 (graft) server.
 * When the AiPoweredSearchV1 flag resolves the surface to "v1" the hook is
 * fully inert — zero requests ever, empty suggestions, enabled=false — so
 * the flag-off experience makes exactly the calls origin/master makes
 * (the vendored legacy server 404s anything under /v2 and has no suggest).
 *
 * Where the route is absent the hook also goes permanently inert (zero
 * further requests, zero UI): on a 404, OR on any response that is not
 * JSON — an SPA catch-all answers unknown GETs with an HTML 200, which
 * previously slipped past the 404-only kill-switch and re-fired a doomed
 * fetch on every keystroke (ephpr round-3 capture).
 *
 * Performance contract (learned in the 8091 SPA): 90ms debounce,
 * per-(builder,query) session cache so backspacing re-hits instantly,
 * stale-while-revalidate (previous suggestions stay while the next
 * fetch flies), AbortController on superseded requests.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { SearchApiVersion } from "../aiSearchFlags";
import { loadDevContext } from "../devContext";

export interface SearchSuggestion {
    text: string;
    entity_type: string;
    id: string;
    layer: string;
}

export function useSearchSuggest(
    searchBaseUrl: string,
    liveQuery: string,
    getToken: (() => Promise<string>) | undefined,
    searchApiVersion: SearchApiVersion,
) {
    // ADO 289472: suggest is a graft-only surface — on the v1 (legacy)
    // selection the hook never fires a request and reports enabled=false.
    const active = searchApiVersion === "v2";
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [corrected, setCorrected] = useState<string | null>(null);
    const deadRef = useRef(false); // endpoint 404'd => deployed env, go inert
    const seqRef = useRef(0);
    const cacheRef = useRef<Map<string, { s: SearchSuggestion[]; c: string | null }>>(new Map());
    const abortRef = useRef<AbortController | null>(null);
    // getToken is consumed via a ref so the effect always calls the CURRENT
    // token getter (useAuthToken rebuilds it when the audience resolves
    // asynchronously) without re-firing the whole suggest pipeline on every
    // rebuild — the review-1529274 fix without the dep-array churn.
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;

    useEffect(() => {
        const q = liveQuery.trim();
        if (!active || deadRef.current || q.length < 2) {
            setSuggestions([]);
            setCorrected(null);
            return;
        }
        const ctx = loadDevContext();
        const cacheKey = `${ctx.builderId ?? ""}|${q.toLowerCase()}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setSuggestions(cached.s);
            setCorrected(cached.c);
            return;
        }
        const my = ++seqRef.current;
        // Abort the superseded request IMMEDIATELY (not inside the debounce
        // callback): the common case is keystroke -> new effect run, and the
        // in-flight fetch for the old prefix must die now, not 90ms later.
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const t = window.setTimeout(() => {
            void (async () => {
                try {
                    const tokenFn = getTokenRef.current;
                    const token = tokenFn ? await tokenFn().catch(() => "") : "";
                    const res = await fetch(
                        `${searchBaseUrl}${searchApiVersion}/search/suggest?q=${encodeURIComponent(q)}`,
                        {
                            signal: ctrl.signal,
                            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                        },
                    );
                    // Inert-gate: route absent. 404 is the honest signal; a
                    // non-JSON 200 is an SPA catch-all serving index.html for
                    // an unknown GET — treat both as "no suggest surface here"
                    // or every keystroke re-fires a doomed request (the HTML
                    // res.json() throw was swallowed by this catch forever).
                    const isJson = (res.headers.get("content-type") ?? "").includes(
                        "application/json",
                    );
                    if (res.status === 404 || (res.ok && !isJson)) {
                        deadRef.current = true; // env without the route: never ask again
                        return;
                    }
                    if (!res.ok) return;
                    const j = await res.json();
                    const next: SearchSuggestion[] = (j.suggestions || []).slice(0, 6);
                    const corr: string | null =
                        j.corrected && j.corrected.toLowerCase() !== q.toLowerCase()
                            ? j.corrected
                            : null;
                    // Staleness check BEFORE the cache write: a response that
                    // lost the race must not poison the cache for its key
                    // (the early-return above trusts cache entries blindly).
                    if (my !== seqRef.current) return;
                    cacheRef.current.set(cacheKey, { s: next, c: corr });
                    if (cacheRef.current.size > 200) cacheRef.current.clear();
                    setSuggestions(next);
                    setCorrected(corr);
                } catch {
                    /* aborted or unreachable */
                }
            })();
        }, 90);
        return () => {
            window.clearTimeout(t);
            ctrl.abort();
        };
    }, [searchBaseUrl, liveQuery, active, searchApiVersion]);

    const reset = useCallback(() => {
        setSuggestions([]);
        setCorrected(null);
    }, []);

    return { suggestions, corrected, reset, enabled: active && !deadRef.current };
}
